// -----------------------------------------------------------------------------
// src/features/transactions/transactionThunks.js
// -----------------------------------------------------------------------------
import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../api/axiosInstance';

/**
 * GET /api/transactions/?store_id=<storeId>&limit=<limit>&offset=<offset>
 * Supports DRF limit/offset pagination.
 * Pass an object { store_id, offset, limit } as the thunk arg.
 * For backward compatibility, also accepts storeId parameter.
 */
export const fetchTransactionsThunk = createAsyncThunk(
  'transactions/fetchAll',
  async ({ store_id = null, storeId = null, offset = 0, limit = 20 } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      params.append('limit', limit);
      params.append('offset', offset);
      
      // Use store_id if provided, fall back to storeId for backward compatibility
      const effectiveStoreId = store_id || storeId;
      if (effectiveStoreId) params.append('store_id', effectiveStoreId);
      
      const response = await axiosInstance.get(`/api/transactions/?${params.toString()}`);
      return response.data; // { count, next, previous, results }
    } catch (error) {
      return rejectWithValue('Failed to fetch transactions');
    }
  }
);

/**
 * GET /api/transactions/<transaction_id>/detail/
 * Fetches a single transaction by ID
 */
export const fetchSingleTransactionThunk = createAsyncThunk(
  'transactions/fetchSingle',
  async (transactionId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/api/transactions/${transactionId}/`);
      return response.data;
    } catch (error) {
      return rejectWithValue(`Failed to fetch transaction: ${error.message}`);
    }
  }
);

/**
 * PATCH /api/transactions/<transaction_id>/
 */
export const updateTransactionThunk = createAsyncThunk(
  'transactions/update',
  async ({ transactionId, updates }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(`/api/transactions/${transactionId}/`, updates);
      return response.data;
    } catch (error) {
      return rejectWithValue('Failed to update transaction');
    }
  }
);

// -----------------------------------------------------------------------------
// Suspicious Transaction Alerts page
// Kept separate from fetchTransactionsThunk so the alerts page, the dashboard
// widget and the store page don't overwrite each other's lists.
// -----------------------------------------------------------------------------

/**
 * Turn the page's filter state into backend query params.
 * Dates are sent as local-day boundaries so "from/to" match the user's calendar day.
 */
export const buildAlertParams = (filters = {}) => {
  const params = {};
  if (filters.store) params.store = filters.store;
  if (filters.device) params.device = filters.device;
  if (filters.status === 'flagged') params.flagged = 'true';
  if (filters.status === 'unflagged') params.flagged = 'false';
  if (filters.review === 'verified') params.verified = 'true';
  if (filters.review === 'unverified') params.verified = 'false';
  if (filters.video === 'with') params.has_video = 'true';
  if (filters.video === 'without') params.has_video = 'false';
  if (filters.dateFrom) params.date_from = new Date(`${filters.dateFrom}T00:00:00`).toISOString();
  if (filters.dateTo) params.date_to = new Date(`${filters.dateTo}T23:59:59.999`).toISOString();
  if (filters.search && filters.search.trim()) params.search = filters.search.trim();
  if (filters.ordering) params.ordering = filters.ordering;
  return params;
};

const describeApiError = (error, fallback) => {
  const data = error?.response?.data;
  if (data && typeof data === 'object') {
    const parts = Object.values(data).flat().filter((v) => typeof v === 'string');
    if (parts.length) return parts.join(' ');
  }
  return fallback;
};

/**
 * GET /api/transactions/?<filters>&limit=&offset=
 * Arg: { filters, offset = 0, limit = 20 }
 */
export const fetchAlertTransactionsThunk = createAsyncThunk(
  'transactions/fetchAlerts',
  async ({ filters = {}, offset = 0, limit = 20 } = {}, { rejectWithValue, signal }) => {
    try {
      const response = await axiosInstance.get('/api/transactions/', {
        params: { ...buildAlertParams(filters), limit, offset },
        signal,
      });
      return response.data; // { count, next, previous, results }
    } catch (error) {
      return rejectWithValue(describeApiError(error, 'Could not load transactions. Check your connection and try again.'));
    }
  }
);

/**
 * GET /api/transactions/filter-options/?store=<id>
 * Returns { stores: [{ id, store_name, store_city, transaction_count, flagged_count }], devices: [] }
 */
export const fetchAlertFilterOptionsThunk = createAsyncThunk(
  'transactions/fetchAlertFilterOptions',
  async ({ store = '' } = {}, { rejectWithValue, signal }) => {
    try {
      const response = await axiosInstance.get('/api/transactions/filter-options/', {
        params: store ? { store } : {},
        signal,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(describeApiError(error, 'Could not load store and device lists.'));
    }
  }
);

/**
 * Fetch every transaction matching the filters (for Excel export).
 * Pages through the API 100 at a time, capped at maxRows.
 */
export const fetchAllAlertTransactions = async (filters, { maxRows = 5000 } = {}) => {
  const rows = [];
  let offset = 0;
  const limit = 100;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { data } = await axiosInstance.get('/api/transactions/', {
      params: { ...buildAlertParams(filters), limit, offset },
    });
    rows.push(...data.results);
    if (!data.next || rows.length >= maxRows || data.results.length === 0) {
      return { rows: rows.slice(0, maxRows), total: data.count };
    }
    offset += limit;
  }
};
